import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Loader2, Save, Pencil, Plus, AlertTriangle, ChevronRight, Trash2, Receipt,
  Wallet, Target, Gauge, CheckCircle2, XCircle, Info, BarChart3, Settings2, Calendar,
} from 'lucide-react';
import ChargeMatrixEditor from '../../../components/contracts/ChargeMatrixEditor';
import { CHARGE_POSTES, POSTE_LABELS, DEFAULT_MATRIX } from '../../../constants/chargePostes';
import ContractMarginTab from './ContractMarginTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NBSP = '\u202f';
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)).replace(/\s/g, NBSP);
const fcfa = (n) => `${fmt(n)} FCFA`;
const fcfaShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace('.', ',')} M FCFA`;
  if (v >= 10_000) return `${Math.round(n / 1000)} k FCFA`;
  return fcfa(n);
};
const monthLabel = (period) => {
  if (!period || period === 'cumul') return 'Cumul du contrat';
  const [y, m] = period.split('-');
  const label = new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};
const pctTxt = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')}${NBSP}%`);

const BEARER_CHIP = {
  baobab: { cls: 'border-gray-900/15 bg-white', text: 'text-gray-900', label: 'Baobab' },
  client: { cls: 'border-sky-200 bg-sky-50/60', text: 'text-sky-700', label: 'À la charge client' },
  included_carrier: { cls: 'border-violet-200 bg-violet-50/60', text: 'text-violet-700', label: 'Inclus prestataire' },
  na: { cls: 'border-gray-200 bg-gray-50', text: 'text-gray-400', label: 'Non concerné' },
};

const QUALITY_BADGE = {
  reliable: { label: 'Fiable', cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  partial: { label: 'Partiellement fiable', cls: 'border-amber-300 text-amber-700 bg-amber-50' },
  unavailable: { label: 'Non exploitable', cls: 'border-gray-300 text-gray-600 bg-gray-50' },
};

const STATE_META = {
  filled: { icon: CheckCircle2, tone: 'text-emerald-600', label: 'Renseigné', text: 'text-emerald-700' },
  missing: { icon: XCircle, tone: 'text-red-500', label: 'Non renseigné', text: 'text-red-600' },
  included: { icon: CheckCircle2, tone: 'text-violet-500', label: 'Inclus prestataire', text: 'text-gray-500' },
  external: { icon: CheckCircle2, tone: 'text-sky-500', label: 'À la charge client', text: 'text-gray-500' },
  na: { icon: Info, tone: 'text-gray-300', label: 'Non concerné', text: 'text-gray-400' },
};

const ROW_STATUS = {
  real: { label: 'Conforme', cls: 'border-emerald-200 text-emerald-700 bg-emerald-50' },
  over_budget: { label: 'Hors budget', cls: 'border-red-200 text-red-700 bg-red-50' },
  missing_cost: { label: 'Coûts à saisir', cls: 'border-amber-200 text-amber-700 bg-amber-50' },
  no_revenue: { label: 'Non facturé', cls: 'border-gray-200 text-gray-500 bg-gray-50' },
};

const BAR_COLORS = ['bg-sky-600', 'bg-amber-500', 'bg-emerald-600', 'bg-violet-600',
  'bg-rose-500', 'bg-teal-600', 'bg-indigo-500', 'bg-orange-500', 'bg-lime-600', 'bg-gray-400'];

const KPI = ({ icon: Icon, label, value, sub, hint, tone = 'neutral', testId }) => {
  const tones = {
    neutral: 'text-gray-900',
    positive: 'text-emerald-700',
    negative: 'text-red-600',
    muted: 'text-gray-400',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm"
         data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-gray-100">
          <Icon className="h-3.5 w-3.5 text-gray-600" />
        </span>
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className={`mt-3 font-mono text-2xl font-bold tracking-tight ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-0.5 font-mono text-[11px] text-gray-400">{sub}</p>}
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
};

const ContractProfitabilityTab = ({ contractId, contract }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [monthlyCosts, setMonthlyCosts] = useState([]);
  const [scope, setScope] = useState('cumul');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [editMatrix, setEditMatrix] = useState(false);
  const [matrixDraft, setMatrixDraft] = useState(DEFAULT_MATRIX);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [costDialog, setCostDialog] = useState(null);
  const [savingCost, setSavingCost] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, costsRes] = await Promise.all([
        axios.get(`${API}/admin/contract-margin/${contractId}/profitability`),
        axios.get(`${API}/admin/contract-margin/${contractId}/monthly-costs`),
      ]);
      setData(profRes.data);
      setMonthlyCosts(costsRes.data?.items || []);
      const m = {};
      Object.entries(profRes.data?.chargeMatrix || {}).forEach(([k, v]) => { m[k] = v.bearer; });
      setMatrixDraft({ ...DEFAULT_MATRIX, ...m });
      setSelectedPeriod((prev) => prev
        || profRes.data?.monthly?.[profRes.data.monthly.length - 1]?.period || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement de la rentabilité');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveMatrix = async () => {
    setSavingMatrix(true);
    try {
      const payload = { ...matrixDraft };
      delete payload.truck_rental;
      await axios.put(`${API}/admin/contract-margin/${contractId}/charge-matrix`, payload);
      toast.success('Répartition des charges enregistrée');
      setEditMatrix(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSavingMatrix(false);
    }
  };

  const monthOptions = useMemo(() => {
    const set = new Set();
    (data?.monthly || []).forEach((r) => set.add(r.period));
    const now = new Date();
    const startRaw = contract?.startDate ? String(contract.startDate).slice(0, 7) : null;
    const endRaw = contract?.endDate ? String(contract.endDate).slice(0, 7) : null;
    const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const maxKey = endRaw && endRaw < nowKey ? endRaw : nowKey;
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (key > maxKey || (startRaw && key < startRaw)) continue;
      set.add(key);
    }
    return [...set].sort().reverse();
  }, [data, contract]);

  const emptyRow = (period) => ({
    period, revenue: 0,
    expectedRevenue: data?.cost?.volumeBased ? null : (data?.revenue?.monthlyPrice ?? null),
    cost: null, costByPoste: {}, volume: null,
    budgetCost: (!data?.cost?.volumeBased && data?.cost?.budgetConfigured)
      ? data.cost.budgetMonthly : null,
    margin: null, marginPct: null, marginBudget: null, marginBudgetPct: null, status: 'no_revenue',
  });

  const historyRows = useMemo(() => {
    const byPeriod = {};
    (data?.monthly || []).forEach((r) => { byPeriod[r.period] = r; });
    return monthOptions.slice(0, 12).map((p) => byPeriod[p] || emptyRow(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, monthOptions]);

  const current = useMemo(() => {
    if (!data) return null;
    if (scope === 'cumul') return data.cumulative;
    return (data.monthly || []).find((r) => r.period === selectedPeriod)
      || emptyRow(selectedPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, scope, selectedPeriod]);

  const openCostDialog = (period) => {
    const existing = monthlyCosts.find((c) => c.period === period);
    const postes = {};
    (data?.lines || []).filter((l) => l.countedInMargin).forEach((l) => {
      postes[l.poste] = existing?.postes?.[l.poste] ?? '';
    });
    setCostDialog({
      period: period || (scope === 'month' ? selectedPeriod : monthOptions[0]),
      isNew: !existing,
      postes,
      partnerName: existing?.partnerName || contract?.transportPartnerName || '',
      partnerInvoiceRef: existing?.partnerInvoiceRef || '',
    });
  };

  const saveCost = async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(costDialog.period || '')) {
      toast.error('Choisis le mois concerné avant d\'enregistrer');
      return;
    }
    setSavingCost(true);
    try {
      const postes = {};
      Object.entries(costDialog.postes).forEach(([k, v]) => {
        if (v !== '' && Number(v) > 0) postes[k] = Number(v);
      });
      await axios.put(
        `${API}/admin/contract-margin/${contractId}/monthly-costs/${costDialog.period}`,
        {
          postes,
          partnerName: costDialog.partnerName || null,
          partnerInvoiceRef: costDialog.partnerInvoiceRef || null,
        },
      );
      toast.success(`Coûts de ${monthLabel(costDialog.period)} enregistrés`);
      setCostDialog(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement du coût');
    } finally {
      setSavingCost(false);
    }
  };

  const deleteCost = async (period) => {
    try {
      await axios.delete(`${API}/admin/contract-margin/${contractId}/monthly-costs/${period}`);
      toast.success('Coûts supprimés');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="profitability-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!data || !current) return null;

  const periodTitle = scope === 'cumul' ? 'Cumul du contrat' : monthLabel(selectedPeriod);
  const quality = data.quality || {};
  const qBadge = QUALITY_BADGE[quality.status] || QUALITY_BADGE.unavailable;
  const isMonthlyEntry = data.completeness?.costEntryMode === 'monthly';
  const volumeBased = !!data.cost?.volumeBased;
  const volumeUnit = data.revenue?.volumeUnit || 'transfert';
  const volumeQty = (v) => (v ? (volumeUnit === 'tonne' ? v.tons : v.runs) : null);
  const unitCost = volumeUnit === 'tonne' ? data.cost?.costPerTon : data.cost?.costPerUnit;
  const unitPrice = volumeUnit === 'tonne' ? data.revenue?.pricePerTon : data.revenue?.pricePerUnit;
  const costUnreliable = quality.status !== 'reliable';

  // Prévu vs Réel — même périmètre des deux côtés
  const cmpRows = [
    { key: 'revenue', label: "Chiffre d'affaires (HT)", budget: current.expectedRevenue,
      real: current.revenue || null, goodWhenPositive: true },
    { key: 'cost', label: 'Coûts totaux', budget: current.budgetCost,
      real: current.cost, goodWhenPositive: false },
    { key: 'margin', label: 'Marge', budget: current.marginBudget, real: current.margin,
      budgetPct: current.marginBudgetPct, realPct: current.marginPct, goodWhenPositive: true },
  ];
  const marginGap = (current.margin !== null && current.marginBudget !== null)
    ? Math.round(current.margin - current.marginBudget) : null;
  // Règle UX : pas de vert « sur-performance » tant que des charges à notre charge manquent.
  const gapReliable = marginGap !== null && !costUnreliable;
  const gapTone = marginGap === null ? 'muted' : (!gapReliable ? 'warn' : (marginGap >= 0 ? 'good' : 'bad'));
  const GAP_STYLE = {
    muted: { card: '', chip: 'bg-gray-100', icon: 'text-gray-500', title: 'text-gray-700', value: 'text-gray-400' },
    warn: { card: 'border-amber-200 bg-amber-50/40', chip: 'bg-amber-100', icon: 'text-amber-600', title: 'text-amber-900', value: 'text-amber-800' },
    good: { card: 'border-emerald-200 bg-emerald-50/40', chip: 'bg-emerald-100', icon: 'text-emerald-600', title: 'text-emerald-900', value: 'text-emerald-900' },
    bad: { card: 'border-red-200 bg-red-50/40', chip: 'bg-red-100', icon: 'text-red-600', title: 'text-red-900', value: 'text-red-700' },
  }[gapTone];
  const revenueGapPct = (current.expectedRevenue && current.revenue)
    ? Math.round((current.revenue - current.expectedRevenue) / Math.abs(current.expectedRevenue) * 1000) / 10
    : null;

  const costTotal = current.cost || 0;
  const costLines = (data.lines || []).filter((l) => l.countedInMargin).map((l, i) => ({
    ...l,
    amount: current.costByPoste?.[l.poste] ?? null,
    color: BAR_COLORS[i % BAR_COLORS.length],
  }));
  const topOverrun = costLines
    .filter((l) => l.amount)
    .sort((a, b) => b.amount - a.amount)[0];

  const cs = data.costStructure || {};
  const fleetCostItems = cs.fleetCostItems || [];

  return (
    <div className="space-y-5" data-testid="contract-profitability-tab">

      {/* ───────── Périmètre d'analyse + qualité des données ───────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2" data-testid="scope-card">
          <CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Périmètre d'analyse
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {[['cumul', 'Cumul du contrat'], ['month', 'Par mois']].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setScope(key)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      scope === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid={`scope-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {scope === 'month' && (
                <Select value={selectedPeriod || ''} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="scope-month-select">
                    <Calendar className="mr-1.5 h-3.5 w-3.5 text-gray-400" />
                    <SelectValue placeholder="Choisir un mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`scope-month-option-${p}`}>
                        {monthLabel(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <p className="mt-2 text-[11px] text-gray-400">
              Toutes les données ci-dessous portent sur ce périmètre.
            </p>
          </CardContent>
        </Card>

        <Card className={`lg:col-span-3 ${costUnreliable ? 'border-amber-200 bg-amber-50/50' : 'border-emerald-200 bg-emerald-50/40'}`}
              data-testid="quality-banner">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-start gap-3">
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                costUnreliable ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                <AlertTriangle className={`h-4 w-4 ${costUnreliable ? 'text-amber-600' : 'text-emerald-600'}`} />
              </span>
              <div>
                <p className={`text-sm font-semibold ${costUnreliable ? 'text-amber-900' : 'text-emerald-900'}`}>
                  {costUnreliable ? 'Données financières incomplètes' : 'Données financières complètes'}
                </p>
                <p className={`text-xs ${costUnreliable ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {quality.missing?.length
                    ? `Charges non renseignées : ${quality.missing.join(', ')}.`
                    : (data.completeness?.missingPeriods?.length
                      ? `${data.completeness.missingPeriods.length} mois facturé(s) sans coût prestataire.`
                      : 'Tous les postes à notre charge sont renseignés.')}
                </p>
              </div>
            </div>
            {isMonthlyEntry && (
              <Button size="sm" variant={costUnreliable ? 'default' : 'outline'}
                      onClick={() => openCostDialog(null)} data-testid="complete-costs-btn">
                Compléter les coûts
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ───────── Répartition des charges ───────── */}
      <Card data-testid="charge-matrix-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
            <span className="flex items-center gap-2">
              Répartition des charges
              {!data.completeness?.matrixConfigured && (
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-700"
                       data-testid="matrix-not-configured">
                  déduite — à confirmer
                </Badge>
              )}
            </span>
            {!editMatrix ? (
              <Button size="sm" variant="outline" onClick={() => setEditMatrix(true)}
                      data-testid="edit-charge-matrix-btn">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifier
              </Button>
            ) : (
              <span className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditMatrix(false)}
                        data-testid="cancel-charge-matrix-btn">Annuler</Button>
                <Button size="sm" onClick={saveMatrix} disabled={savingMatrix}
                        data-testid="save-charge-matrix-btn">
                  {savingMatrix ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                : <Save className="mr-1.5 h-3.5 w-3.5" />}
                  Enregistrer
                </Button>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editMatrix ? (
            <ChargeMatrixEditor value={matrixDraft} onChange={setMatrixDraft} />
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
                 data-testid="charge-matrix-summary">
              {CHARGE_POSTES.map((p) => {
                const entry = data.chargeMatrix?.[p.key];
                if (!entry) return null;
                const chip = BEARER_CHIP[entry.bearer] || BEARER_CHIP.na;
                return (
                  <div key={p.key} className={`rounded-lg border p-2.5 ${chip.cls}`}
                       data-testid={`matrix-chip-${p.key}`}>
                    <p className="truncate text-[10px] font-medium uppercase tracking-wide text-gray-400">
                      {p.label}
                    </p>
                    <p className={`mt-1 text-xs font-semibold leading-tight ${chip.text}`}>
                      {chip.label}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───────── Performance financière ───────── */}
      <Card data-testid="performance-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Performance financière — {periodTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <KPI
              icon={Receipt}
              label="CA facturé (HT)"
              value={current.revenue ? fcfaShort(current.revenue) : 'Non facturé'}
              sub={current.revenue ? fcfa(current.revenue) : null}
              testId="kpi-revenue"
            />
            <KPI
              icon={Wallet}
              label="Coûts réels"
              value={current.cost === null ? 'Non renseigné' : fcfaShort(current.cost)}
              sub={current.cost === null ? null : fcfa(current.cost)}
              tone={current.cost === null ? 'muted' : 'neutral'}
              hint={current.cost === null ? 'Aucune facture prestataire saisie sur ce périmètre' : null}
              testId="kpi-cost"
            />
            <KPI
              icon={Gauge}
              label="Marge réelle"
              value={current.margin === null ? 'n/d' : fcfaShort(current.margin)}
              sub={current.margin === null ? null : `${fcfa(current.margin)} · ${pctTxt(current.marginPct)}`}
              tone={current.margin === null ? 'muted'
                : (current.margin < 0 ? 'negative' : (costUnreliable ? 'neutral' : 'positive'))}
              hint={current.margin !== null && costUnreliable
                ? 'Marge non fiable : des charges sont absentes' : null}
              testId="kpi-margin-real"
            />
            <KPI
              icon={Target}
              label="Marge budgétée"
              value={current.marginBudget === null ? 'Non renseigné' : fcfaShort(current.marginBudget)}
              sub={current.marginBudget === null ? null
                : `${fcfa(current.marginBudget)} · ${pctTxt(current.marginBudgetPct)}`}
              tone={current.marginBudget === null ? 'muted' : 'neutral'}
              hint={current.marginBudget === null
                ? 'Aucun budget de coût défini dans le modèle financier' : null}
              testId="kpi-margin-budget"
            />
          </div>
        </CardContent>
      </Card>

      {/* ───────── Prévu vs Réel ───────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="cmp-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Prévu vs Réel — {periodTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cmp-table">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="py-2 pr-3 text-left font-medium">Indicateur</th>
                    <th className="px-3 py-2 text-right font-medium">Budget</th>
                    <th className="px-3 py-2 text-right font-medium">Réel</th>
                    <th className="px-3 py-2 text-right font-medium">Écart</th>
                    <th className="py-2 pl-3 text-right font-medium">Écart %</th>
                  </tr>
                </thead>
                <tbody>
                  {cmpRows.map((r) => {
                    const comparable = r.budget !== null && r.budget !== undefined
                      && r.real !== null && r.real !== undefined;
                    const delta = comparable ? Math.round(r.real - r.budget) : null;
                    const deltaPct = comparable && r.budget
                      ? Math.round((r.real - r.budget) / Math.abs(r.budget) * 1000) / 10 : null;
                    const good = delta === null ? null
                      : (r.goodWhenPositive ? delta >= 0 : delta <= 0);
                    return (
                      <tr key={r.key} className={`border-b border-gray-50 ${r.key === 'margin' ? 'bg-gray-50/70' : ''}`}
                          data-testid={`cmp-row-${r.key}`}>
                        <td className={`py-2.5 pr-3 ${r.key === 'margin' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {r.label}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-gray-900">
                          {r.budget === null || r.budget === undefined
                            ? <span className="font-sans text-xs text-gray-400">Non renseigné</span>
                            : fmt(r.budget)}
                          {r.key === 'margin' && r.budgetPct !== null && r.budgetPct !== undefined && (
                            <span className="block text-[10px] font-sans text-gray-500">{pctTxt(r.budgetPct)}</span>
                          )}
                        </td>
                        <td className={`px-3 py-2.5 text-right font-mono ${
                          (r.real || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {r.real === null || r.real === undefined
                            ? <span className="font-sans text-xs text-gray-400">Non renseigné</span>
                            : fmt(r.real)}
                          {r.key === 'margin' && r.realPct !== null && r.realPct !== undefined && (
                            <span className="block text-[10px] font-sans text-gray-500">{pctTxt(r.realPct)}</span>
                          )}
                        </td>
                        <td className={`px-3 py-2.5 text-right font-mono font-semibold ${
                          good === null ? 'text-gray-400' : good ? 'text-emerald-700' : 'text-red-600'}`}>
                          {delta === null
                            ? <span className="font-sans text-xs">non comparable</span>
                            : `${delta >= 0 ? '+' : '−'}${fmt(Math.abs(delta))}`}
                        </td>
                        <td className={`py-2.5 pl-3 text-right font-mono text-xs ${
                          good === null ? 'text-gray-400' : good ? 'text-emerald-700' : 'text-red-600'}`}>
                          {deltaPct === null ? '—' : `${deltaPct >= 0 ? '+' : '−'}${pctTxt(Math.abs(deltaPct))}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-gray-500">
              Budget et réel portent sur le même périmètre ({periodTitle.toLowerCase()}).
              {volumeBased
                ? ` Prévu calculé sur le volume (${unitCost ? `${fcfa(unitCost)}/${volumeUnit} de coût` : 'coût unitaire non renseigné'}${
                  data.revenue?.targetMonthlyVolume
                    ? `, CA prévu sur un volume cible de ${fmt(data.revenue.targetMonthlyVolume)} ${volumeUnit}(s)/mois`
                    : ', CA prévu sur le volume réalisé'}).`
                : (scope === 'cumul' && data.cumulative?.monthsInvoiced
                  ? ` Budget calculé sur ${data.cumulative.monthsInvoiced} mois facturé(s).` : '')}
            </p>
            {!volumeBased && revenueGapPct !== null && Math.abs(revenueGapPct) > 25 && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3"
                   data-testid="cmp-billing-deviation">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                <p className="text-xs text-sky-900">
                  La facturation s'écarte de {revenueGapPct >= 0 ? '+' : ''}{pctTxt(revenueGapPct)} du
                  prix mensuel de référence du contrat ({fcfa(data.revenue?.monthlyPrice)}/mois) — souvent
                  un avenant, des camions supplémentaires ou une facturation au réel. Le « budget » de CA
                  ci-dessus reste basé sur ce prix de référence tant qu'il n'est pas mis à jour.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={GAP_STYLE.card} data-testid="cmp-synthesis-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <span className={`grid h-9 w-9 place-items-center rounded-lg ${GAP_STYLE.chip}`}>
                <BarChart3 className={`h-4 w-4 ${GAP_STYLE.icon}`} />
              </span>
              <p className={`text-sm font-semibold ${GAP_STYLE.title}`}>
                {marginGap === null ? 'Écart non comparable'
                  : (!gapReliable ? 'Écart non fiable — charges manquantes'
                    : (marginGap >= 0 ? 'Sur-performance' : 'Sous-performance'))}
              </p>
            </div>
            <p className={`mt-3 font-mono text-2xl font-bold ${GAP_STYLE.value}`}
               data-testid="cmp-synthesis-value">
              {marginGap === null ? 'n/d'
                : `${marginGap >= 0 ? '+' : '−'}${fcfa(Math.abs(marginGap))}`}
            </p>
            <p className="text-[11px] text-gray-500">vs budget</p>
            <p className="mt-3 text-xs leading-relaxed text-gray-700" data-testid="cmp-synthesis-reason">
              {marginGap === null
                ? (current.cost === null
                  ? 'Les coûts réels du périmètre ne sont pas saisis : aucun écart ne peut être calculé.'
                  : 'Aucun budget de coût n\'est défini dans le modèle financier.')
                : (!gapReliable
                  ? `Écart indicatif seulement : ${(quality.missing || []).join(', ')} `
                    + 'ne sont pas renseignés, l\'écart se dégradera après leur saisie.'
                  : (marginGap < 0 && topOverrun
                    ? `La sous-performance provient principalement du poste ${topOverrun.label.toLowerCase()}, supérieur au budget.`
                    : (marginGap < 0
                      ? 'Les coûts réels dépassent le budget sur ce périmètre.'
                      : 'Les coûts réels restent sous le budget sur ce périmètre.')))}
            </p>
            <Button size="sm" variant="outline" className="mt-4 w-full"
                    onClick={() => setShowModel(true)} data-testid="analyse-gap-btn">
              Analyser l'écart
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ───────── Structure des coûts + qualité des données ───────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card data-testid="cost-structure-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
              <span>Structure des coûts réels — {periodTitle}</span>
              <span className="font-mono text-xs font-normal text-gray-500">
                Total : {current.cost === null ? 'non renseigné' : fcfa(costTotal)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {current.cost === null ? (
              <p className="py-6 text-center text-sm text-gray-400" data-testid="cost-structure-empty">
                Aucun coût saisi sur ce périmètre — la répartition ne peut pas être affichée.
              </p>
            ) : (
              <>
                <div className="flex h-7 w-full overflow-hidden rounded-lg bg-gray-100"
                     data-testid="cost-structure-bar">
                  {costLines.filter((l) => l.amount).map((l) => {
                    const pct = costTotal ? Math.round(l.amount / costTotal * 1000) / 10 : 0;
                    return (
                      <div key={l.poste}
                           className={`flex items-center justify-center ${l.color} transition-all`}
                           style={{ width: `${pct}%` }}
                           title={`${l.label} — ${fcfa(l.amount)}`}>
                        {pct >= 8 && (
                          <span className="px-1 text-[10px] font-semibold text-white">{Math.round(pct)}%</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 space-y-1.5">
                  {costLines.map((l) => {
                    const pct = costTotal && l.amount ? Math.round(l.amount / costTotal * 1000) / 10 : null;
                    return (
                      <div key={l.poste} className="flex items-center gap-2 text-sm"
                           data-testid={`cost-line-${l.poste}`}>
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${l.amount ? l.color : 'bg-gray-200'}`} />
                        <span className="flex-1 truncate text-gray-700">{l.label}</span>
                        <span className={`font-mono text-xs ${l.amount ? 'text-gray-900' : 'text-amber-600'}`}>
                          {l.amount ? fcfa(l.amount) : 'Non renseigné'}
                        </span>
                        <span className="w-12 text-right font-mono text-xs text-gray-500">
                          {pct === null ? '—' : `${pct}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card data-testid="quality-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
              <span>Qualité des données</span>
              <Badge variant="outline" className={`text-[11px] ${qBadge.cls}`} data-testid="quality-status">
                {qBadge.label}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-gray-500">
              {quality.postesFilled ?? 0} poste(s) renseigné(s) sur {quality.postesExpected ?? 0}
              {' '}à notre charge.
            </p>
            <div className="space-y-1.5">
              {(quality.postes || []).map((q) => {
                const meta = STATE_META[q.state] || STATE_META.na;
                const Icon = meta.icon;
                return (
                  <div key={q.poste} className="flex items-center gap-2 text-sm"
                       data-testid={`quality-line-${q.poste}`}>
                    <Icon className={`h-4 w-4 shrink-0 ${meta.tone}`} />
                    <span className="flex-1 truncate text-gray-700">{q.label}</span>
                    <span className={`text-xs ${meta.text}`}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ───────── Historique mensuel ───────── */}
      <Card data-testid="history-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
            <span>Historique mensuel</span>
            {isMonthlyEntry && (
              <Button size="sm" onClick={() => openCostDialog(null)} data-testid="add-monthly-cost-btn">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Ajouter des coûts
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!isMonthlyEntry && (
            <p className="mb-3 text-xs text-gray-500" data-testid="per-run-cost-info">
              Contrat facturé à l'unité / au tonnage : les coûts se saisissent sur chaque course
              ({data.completeness?.runsWithCost || 0} sur {data.completeness?.totalRuns || 0} renseignée(s)).
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-table">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3 text-left font-medium">Mois</th>
                  {volumeBased && (
                    <th className="px-3 py-2 text-right font-medium">
                      Volume ({volumeUnit === 'tonne' ? 't' : 'transferts'})
                    </th>
                  )}
                  <th className="px-3 py-2 text-right font-medium">CA facturé (HT)</th>
                  <th className="px-3 py-2 text-right font-medium">Budget coûts</th>
                  <th className="px-3 py-2 text-right font-medium">Coûts réels</th>
                  <th className="px-3 py-2 text-right font-medium">Marge</th>
                  <th className="px-3 py-2 text-right font-medium">Marge %</th>
                  <th className="px-3 py-2 text-left font-medium">Statut</th>
                  <th className="py-2 pl-3" />
                </tr>
              </thead>
              <tbody>
                {historyRows.length === 0 && (
                  <tr><td colSpan={volumeBased ? 9 : 8} className="py-6 text-center text-sm text-gray-400">
                    Aucun mois à afficher sur la période du contrat
                  </td></tr>
                )}
                {historyRows.map((row) => {
                  const st = ROW_STATUS[row.status] || ROW_STATUS.no_revenue;
                  const active = scope === 'month' && selectedPeriod === row.period;
                  return (
                    <tr
                      key={row.period}
                      onClick={() => { setScope('month'); setSelectedPeriod(row.period); }}
                      className={`cursor-pointer border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                        active ? 'bg-sky-50/60' : ''}`}
                      data-testid={`history-row-${row.period}`}
                    >
                      <td className={`py-2.5 pr-3 font-medium ${active ? 'text-sky-700' : 'text-gray-900'}`}>
                        {monthLabel(row.period)}
                      </td>
                      {volumeBased && (
                        <td className="px-3 py-2.5 text-right font-mono" data-testid={`history-volume-${row.period}`}>
                          {volumeQty(row.volume)
                            ? <>
                              {fmt(volumeQty(row.volume))}
                              {volumeUnit === 'tonne' && row.volume?.runsMissingWeight > 0 && (
                                <span className="block text-[10px] font-sans text-amber-600">
                                  {row.volume.runsMissingWeight} course(s) sans poids
                                </span>
                              )}
                            </>
                            : <span className="text-gray-300">—</span>}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right font-mono">
                        {row.revenue ? fmt(row.revenue) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {row.budgetCost === null ? <span className="text-gray-300">—</span> : fmt(row.budgetCost)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {row.cost === null ? <span className="text-gray-300">—</span> : fmt(row.cost)}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono ${
                        row.margin === null ? '' : (row.margin < 0 ? 'text-red-600' : 'text-emerald-700')}`}>
                        {row.margin === null ? <span className="text-gray-300">—</span> : fmt(row.margin)}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono text-xs ${
                        row.marginPct === null ? '' : (row.marginPct < 0 ? 'text-red-600' : 'text-emerald-700')}`}>
                        {row.marginPct === null ? <span className="text-gray-300">—</span> : pctTxt(row.marginPct)}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                      </td>
                      <td className="py-2.5 pl-3 text-right">
                        <span className="flex items-center justify-end gap-0.5">
                          {isMonthlyEntry && row.cost !== null && (
                            <Button size="sm" variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); deleteCost(row.period); }}
                                    data-testid={`delete-cost-${row.period}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          )}
                          {isMonthlyEntry && (
                            <Button size="sm" variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); openCostDialog(row.period); }}
                                    data-testid={`edit-cost-${row.period}`}>
                              <Pencil className="h-3.5 w-3.5 text-gray-500" />
                            </Button>
                          )}
                          <ChevronRight className="h-4 w-4 text-gray-300" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ───────── Paramètres du modèle financier ───────── */}
      <Card data-testid="financial-model-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
            <span className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-gray-500" /> Paramètres du modèle financier
            </span>
            <Button size="sm" variant="outline" onClick={() => setShowModel((v) => !v)}
                    data-testid="toggle-cfo-details">
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> {showModel ? 'Fermer' : 'Modifier'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-xs text-gray-500">
            Ces paramètres servent au calcul du budget et des comparaisons Prévu vs Réel.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {volumeBased ? (
              <>
                <div data-testid="model-unit-cost">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Coût par {volumeUnit}
                  </p>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {unitCost ? `${fcfa(unitCost)} / ${volumeUnit}` : 'Non renseigné'}
                  </p>
                </div>
                <div data-testid="model-unit-price">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Prix client par {volumeUnit}
                  </p>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {unitPrice ? `${fcfa(unitPrice)} / ${volumeUnit}` : 'Non renseigné'}
                  </p>
                </div>
                <div data-testid="model-target-volume">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Volume mensuel cible
                  </p>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {data.revenue?.targetMonthlyVolume
                      ? `${fmt(data.revenue.targetMonthlyVolume)} ${volumeUnit}(s) / mois`
                      : 'Non renseigné (prévu = volume réalisé)'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div data-testid="model-monthly-fixed">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Coût forfaitaire mensuel
                  </p>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {cs.monthlyFixedCost ? `${fcfa(cs.monthlyFixedCost)} / mois` : 'Non renseigné'}
                  </p>
                </div>
                <div data-testid="model-fleet">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Flotte louée</p>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {fleetCostItems.filter((it) => Number(it.monthlyCostPerTruck) > 0).length
                      ? fleetCostItems.filter((it) => Number(it.monthlyCostPerTruck) > 0)
                        .map((it) => `${it.quantity} × ${fmt(it.monthlyCostPerTruck)}`).join(' + ')
                      : 'Non renseigné'}
                  </p>
                </div>
              </>
            )}
            <div data-testid="model-tolls">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Péages budgétés</p>
              <p className="mt-1 font-mono text-sm text-gray-900">
                {cs.tollsBudgetMonthly ? `${fcfa(cs.tollsBudgetMonthly)} / mois`
                  : (data.chargeMatrix?.tolls?.bearer === 'baobab' ? 'Non renseigné'
                    : data.chargeMatrix?.tolls?.bearerLabel)}
              </p>
            </div>
            <div data-testid="model-other">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Autres charges budgétées
              </p>
              <p className="mt-1 font-mono text-sm text-gray-900">
                {cs.otherBudgetMonthly ? `${fcfa(cs.otherBudgetMonthly)} / mois` : 'Non renseigné'}
              </p>
            </div>
          </div>
          <p className="mt-4 font-mono text-xs text-gray-500" data-testid="model-budget-total">
            Budget retenu (postes à notre charge) :{' '}
            {volumeBased
              ? (unitCost
                ? `${fcfa(unitCost)} / ${volumeUnit}${data.cost?.budgetMonthly
                  ? ` + ${fcfa(data.cost.budgetMonthly)} / mois` : ''}`
                : 'Non renseigné')
              : (data.cost?.budgetConfigured ? `${fcfa(data.cost.budgetMonthly)} / mois` : 'Non renseigné')}
          </p>

          {showModel && (
            <div className="mt-6 border-t border-gray-100 pt-6" data-testid="cfo-details-panel">
              <ContractMarginTab contractId={contractId} contract={contract} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───────── Dialogue de saisie du coût mensuel ───────── */}
      <Dialog open={!!costDialog} onOpenChange={(o) => !o && setCostDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="monthly-cost-dialog">
          <DialogHeader>
            <DialogTitle>Coûts réels du mois</DialogTitle>
          </DialogHeader>
          {costDialog && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Mois concerné</Label>
                <Select
                  value={costDialog.period}
                  onValueChange={(v) => setCostDialog({
                    ...costDialog,
                    period: v,
                    isNew: !monthlyCosts.find((c) => c.period === v),
                    postes: Object.keys(costDialog.postes).reduce((acc, k) => ({
                      ...acc,
                      [k]: monthlyCosts.find((c) => c.period === v)?.postes?.[k] ?? '',
                    }), {}),
                  })}
                >
                  <SelectTrigger className="mt-1" data-testid="cost-period-input">
                    <SelectValue placeholder="Choisir un mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`cost-period-option-${p}`}>
                        {monthLabel(p)}
                        {monthlyCosts.find((c) => c.period === p) ? ' — coût déjà saisi' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!costDialog.isNew && (
                  <p className="mt-1 text-[11px] text-amber-700" data-testid="cost-replace-warning">
                    Ce mois a déjà un coût enregistré : l'enregistrement le remplace
                    (une seule ligne de coût par mois).
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.keys(costDialog.postes).map((poste) => (
                  <div key={poste}>
                    <Label className="text-xs">{POSTE_LABELS[poste]}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={costDialog.postes[poste]}
                      onChange={(e) => setCostDialog({
                        ...costDialog,
                        postes: { ...costDialog.postes, [poste]: e.target.value },
                      })}
                      className="mt-1 h-9 font-mono"
                      data-testid={`cost-input-${poste}`}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Prestataire</Label>
                  <Input
                    value={costDialog.partnerName}
                    onChange={(e) => setCostDialog({ ...costDialog, partnerName: e.target.value })}
                    className="mt-1 h-9"
                    data-testid="cost-partner-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">N° facture prestataire</Label>
                  <Input
                    value={costDialog.partnerInvoiceRef}
                    onChange={(e) => setCostDialog({ ...costDialog, partnerInvoiceRef: e.target.value })}
                    className="mt-1 h-9 font-mono"
                    data-testid="cost-invoice-ref-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                Seuls les postes à notre charge sont proposés : ceux payés par le client ou inclus
                dans le prix du prestataire sont volontairement absents.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCostDialog(null)}>Annuler</Button>
            <Button onClick={saveCost} disabled={savingCost} data-testid="save-monthly-cost-btn">
              {savingCost ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          : <Save className="mr-2 h-4 w-4" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractProfitabilityTab;
